import { take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { Navigation } from 'app/core/navigation/navigation.types';
import { User } from 'app/core/Models/user';
import { UserService } from 'app/core/user/user.service';
import { InitialNavigations } from 'app/navigation/navigation-provider';
import
{
    FuseNavigationItem,
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { TranslocoService } from '@ngneat/transloco';

@Injectable({
    providedIn: 'root',
})
export class NavigationService
{
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);
    private _navigationValue: Navigation;

    constructor(
        private userService: UserService,
        private _translocoService: TranslocoService,
        private _fuseNavigationService: FuseNavigationService
    ) // private spService: SpService
    {
        this.navigation = InitialNavigations.navigations;
    }

    get navigation$(): Observable<Navigation>
    {
        return this._navigation.asObservable();
    }

    set navigation(nav: Navigation)
    {
        this._navigation.next(nav);
        this._navigationValue = nav;
    }

    /**
     * This is only for appresolver
     */
    get = (): Observable<Navigation> => of(InitialNavigations.navigations);

    updateNavigationByUserPermissions()
    {
        this.userService.user$.subscribe(_user =>
        {
            let navFiltered: Navigation = this._navigationValue;
            if (navFiltered)
            {
                navFiltered.default = this.filterByUserPermission(
                    navFiltered.default,
                    _user
                );

                this.navigation = navFiltered;

                this._fuseNavigationService
                    .getMainNavigation()
                    .pipe(take(1))
                    .subscribe({
                        next: (
                            navVComponent:
                                | FuseVerticalNavigationComponent
                        ) =>
                        {
                            navVComponent.refresh();
                        },
                    });
            }
        });
    }

    /**
     * Update the navigation
     *
     * @param lang
     */

    public updateNavigation(lang: string): void
    {
        // public updateNavigation(): void {
        // For the demonstration purposes, we will only update the Dashboard names
        // from the navigation but you can do a full swap and change the entire
        // navigation data.
        //
        // You can import the data from a file or request it from your backend,
        // it's up to you.

        // Get the component -> navigation data -> item
        this._fuseNavigationService
            .getMainNavigation()
            .pipe(take(1))
            .subscribe({
                next: (
                    navVComponent:
                        | FuseVerticalNavigationComponent
                ) =>
                {
                    let copy = { ...this._navigationValue };
                    let array = [
                        copy.default,
                    ];
                    for (const all of array)
                    {
                        this._changeTitle(all, lang);
                    }

                    this.navigation = copy;
                    navVComponent.refresh();
                },
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * @private
     */
    private filterByUserPermission(
        items: FuseNavigationItem[],
        _user: User
    ): FuseNavigationItem[]
    {
        return items?.map(item =>
        {
            let permitted = true;

            if (item.hasOwnProperty('key'))
                permitted = item.key.includes(_user?.mainRole);

            if (!permitted) return {} as FuseNavigationItem;

            if (permitted && item.children?.length > 0)
                item.children = this.filterByUserPermission(
                    item.children,
                    _user
                );

            return item;
        });
    }

    private _changeTitle(navigation: FuseNavigationItem[], lang: string): void
    {
        for (const navItem of navigation)
        {
            if (navItem.children) this._changeTitle(navItem.children, lang);

            if (!navItem?.translate) continue;

            let item: FuseNavigationItem = this._fuseNavigationService.getItem(
                navItem.id,
                navigation
            );

            if (item)
            {
                let copy = `${navItem.translate}`;
                const translationKey = `nav.${copy.replace('-', '_')}`;
                const translation = this._translocoService.translate(
                    translationKey,
                    null,
                    lang
                );

                // Set the title
                if (translation && navItem.id != 'nav.' + translation)
                    item.title = translation;
            }
        }
    }
}
